/**
 * The soundtrack.
 *
 * Everything else this game makes is synthesised at runtime; this is the one
 * place real recorded audio is played, so it is deliberately kept apart from
 * the rest of the graph rather than threaded through it.
 *
 * Two decisions worth knowing:
 *
 * **It bypasses the master muffle.** Every diegetic sound in `audio.ts` runs
 * through a 1350 Hz low-pass, because everything you hear underwater has water
 * between it and you. Score does not: it is not in the world, it is over it, and
 * putting a composed track through that filter takes the top off it and makes it
 * sound like it is playing in the next room.
 *
 * **It loops through Web Audio, not through `<audio loop>`.** A media element's
 * loop has a gap — it has to seek — and every lossy codec adds padding at one or
 * both ends, so a track that loops perfectly in a DAW does not loop perfectly
 * once encoded. `AudioBufferSourceNode` loops decoded PCM sample-accurately, and
 * `loopEnd` is set from the *master's* true duration rather than the encoded
 * file's, which is what discards the codec's tail. The Opus file measures 6.5 ms
 * longer than the WAV it came from; unhandled, that is an audible hiccup once
 * every two minutes.
 */

export type TrackRole = 'title' | 'dive' | 'both';

export interface Track {
  id: string;
  title: string;
  /** Where it belongs. `both` plays across the title/dive boundary uninterrupted. */
  role: TrackRole;
  /**
   * True length of the master, in seconds — NOT the length of the encoded file.
   * This is the loop point, and it is the whole reason the seam is inaudible.
   */
  duration: number;
  /** Loop window inside the track, if it is not simply the whole thing. */
  loopStart?: number;
}

/**
 * Add tracks here. `role` decides where each can be heard, and the dive picks
 * from its pool at random so a long session does not become one tune.
 */
export const TRACKS: Track[] = [
  { id: 'abyss-theme', title: 'ABYSS', role: 'both', duration: 124.71712 },
];

/** Opus where it is understood, AAC where it is not. */
function preferredExt(): 'ogg' | 'm4a' {
  const probe = document.createElement('audio');
  return probe.canPlayType('audio/ogg; codecs=opus') ? 'ogg' : 'm4a';
}

export class Music {
  /** 0..1 master level for score, before ducking. */
  level = 0.55;

  private ctx: AudioContext;
  private out: GainNode;
  private buffers = new Map<string, AudioBuffer>();
  private loading = new Map<string, Promise<AudioBuffer | null>>();
  private source: AudioBufferSourceNode | null = null;
  private gain: GainNode | null = null;
  private currentId = '';
  private wanted = '';
  private ext = preferredExt();
  private duckAmount = 0;
  private muted = false;

  constructor(ctx: AudioContext, destination: AudioNode) {
    this.ctx = ctx;
    this.out = ctx.createGain();
    this.out.gain.value = 0;
    this.out.connect(destination);
  }

  get playing(): string { return this.currentId; }

  /** Fetch and decode, once. Returns null if the file is missing or unplayable. */
  private load(id: string): Promise<AudioBuffer | null> {
    const have = this.buffers.get(id);
    if (have) return Promise.resolve(have);
    const inFlight = this.loading.get(id);
    if (inFlight) return inFlight;

    const p = (async () => {
      try {
        const res = await fetch(`/music/${id}.${this.ext}`);
        if (!res.ok) throw new Error(`${res.status}`);
        const bytes = await res.arrayBuffer();
        const buf = await this.ctx.decodeAudioData(bytes);
        this.buffers.set(id, buf);
        return buf;
      } catch (err) {
        // Music is never load-bearing. A missing or undecodable track means a
        // quiet game, not a broken one.
        console.warn(`[abyss] music ${id}.${this.ext} unavailable`, err);
        return null;
      }
    })();
    this.loading.set(id, p);
    return p;
  }

  /** Start warming a track's buffer without playing it. */
  preload(id: string) { void this.load(id); }

  /**
   * Play a track, crossfading from whatever is on. Asking for the track that is
   * already playing does nothing at all — which is what lets a `both` track run
   * across the title-to-dive transition without a seam.
   */
  async play(id: string, fade = 2.0) {
    if (this.currentId === id) return;
    this.wanted = id;
    const track = TRACKS.find((t) => t.id === id);
    if (!track) return;

    const buf = await this.load(id);
    // Something else was asked for while this was decoding.
    if (!buf || this.wanted !== id) return;

    this.stop(fade);

    const g = this.ctx.createGain();
    g.gain.value = 0;
    g.connect(this.out);

    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    src.loopStart = track.loopStart ?? 0;
    // The master's duration, not the file's. Anything the encoder added past
    // this is padding and must never be heard.
    src.loopEnd = Math.min(track.duration, buf.duration);
    src.connect(g);
    src.start();

    const t = this.ctx.currentTime;
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(1, t + fade);

    this.source = src;
    this.gain = g;
    this.currentId = id;
    this.applyLevel();
  }

  /** Pick something for a role. Random within the pool, so a long dive varies. */
  playFor(role: 'title' | 'dive', fade = 2.0) {
    const pool = TRACKS.filter((t) => t.role === role || t.role === 'both');
    if (!pool.length) return;
    // Already playing something valid here? Leave it be.
    if (pool.some((t) => t.id === this.currentId)) return;
    void this.play(pool[Math.floor(Math.random() * pool.length)].id, fade);
  }

  stop(fade = 1.5) {
    const src = this.source;
    const g = this.gain;
    this.source = null;
    this.gain = null;
    this.currentId = '';
    if (!src || !g) return;
    const t = this.ctx.currentTime;
    g.gain.cancelScheduledValues(t);
    g.gain.setValueAtTime(g.gain.value, t);
    g.gain.linearRampToValueAtTime(0, t + fade);
    // Stop a beat after the fade lands, so the node is released rather than
    // left running silently for the rest of the session.
    try { src.stop(t + fade + 0.1); } catch { /* already stopped */ }
  }

  /**
   * Pull the score down under pressure, 0..1. The procedural drone bed in
   * `audio.ts` rises with the same signal, so the two hand over rather than
   * piling up: exploration is scored, a fight is not.
   */
  duck(amount: number) {
    this.duckAmount = amount;
    this.applyLevel();
  }

  setMuted(m: boolean) {
    this.muted = m;
    this.applyLevel();
  }

  private applyLevel() {
    const t = this.ctx.currentTime;
    const target = this.muted ? 0 : this.level * (1 - this.duckAmount * 0.72);
    this.out.gain.setTargetAtTime(target, t, 0.4);
  }
}
